import React, { useEffect } from "react";
import styled from "styled-components";
import { RATypography } from "@/components/customs";
import { RAImage } from "@/components/customs";
import { Col, Row, Grid } from "antd";
import { IconSpeedSvg, IconTimeSvg, IconUserGroupSvg } from "@/assets/images";
import dynamic from "next/dynamic";

const Odometer = dynamic(import("react-odometerjs"), {
	ssr: false,
	loading: () => 0,
});
const { useBreakpoint } = Grid;

export function AircraftSpecifications({ description = "", properties = {}, active = false }) {
	const {
		cruising_speed,
		cruising_speed_symbol = "",
		cruising_speed_unit = "",
		max_fly_time,
		max_fly_time_symbol = "",
		max_fly_time_unit = "",
		max_pax,
		max_pax_symbol = "",
		max_pax_unit = "",
	} = properties;
	const screens = useBreakpoint();

	return (
		<Container
			style={{
				width: "100%",
				top: "0",
				borderRadius: active ? "0" : "16px",
			}}
			id="aircraft-specifications"
		>
			<WrapperContent align="middle" justify="start">
				<Col xxl={8} xl={8} lg={10} md={24} sm={24} xs={24}>
					<PaddingRow style={{ height: "100%" }} align="middle">
						<div>
							<RATypography variant={screens?.md ? "h2" : "h4"} color="#fff">
								Specifications
							</RATypography>
							<RATypography variant="body3" color="#fff" whiteSpace="pre-wrap" top={8} maxLines={5}>
								{description}
							</RATypography>
						</div>
					</PaddingRow>
				</Col>
				<Col xxl={16} xl={16} lg={14} md={24} sm={24} xs={24}>
					<PaddingRow style={{ width: "100%", height: "100%" }}>
						<SpecificationCard xxl={8} xl={8} lg={8} md={8} sm={8} xs={24}>
							<WrapperItem align="middle">
								<Col xxl={24} xl={24} lg={24} md={24} sm={24} style={{ marginRight: 24 }}>
									<RAImage url={IconUserGroupSvg} width={52} height={52} sizes="52px" />
								</Col>
								<Col id="test">
									<div style={{ marginTop: 8 }}>
										<Odometer duration={3000} value={active ? parseFloat(max_pax) : 0} />
										<TextNoAnimation>{`${max_pax_symbol} ${max_pax_unit}`}</TextNoAnimation>
									</div>
									<RATypography variant="body2" top={4}>
										Passengers
									</RATypography>
								</Col>
							</WrapperItem>
						</SpecificationCard>
						<SpecificationCard xxl={8} xl={8} lg={8} md={8} sm={8} xs={24}>
							<WrapperItem align="middle">
								<Col xxl={24} xl={24} lg={24} md={24} sm={24} style={{ marginRight: 24 }}>
									<RAImage url={IconTimeSvg} width={52} height={52} sizes="50px" />
								</Col>
								<Col>
									<div style={{ marginTop: 8 }}>
										<Odometer duration={3000} value={active ? parseFloat(max_fly_time) : 0} />
										<TextNoAnimation>
											{`${max_fly_time_symbol} ${max_fly_time_unit}`}
										</TextNoAnimation>
									</div>
									<RATypography variant="body2" top={4}>
										Range
									</RATypography>
								</Col>
							</WrapperItem>
						</SpecificationCard>
						<SpecificationCard xxl={8} xl={8} lg={8} md={8} sm={8} xs={24}>
							<WrapperItem align="middle">
								<Col xxl={24} xl={24} lg={24} md={24} sm={24} style={{ marginRight: 24 }}>
									<RAImage url={IconSpeedSvg} width={52} height={52} sizes="52px" />
								</Col>
								<Col>
									<div style={{ marginTop: 8 }}>
										<Odometer duration={3000} value={active ? parseFloat(cruising_speed) : 0} />
										<TextNoAnimation>
											{`${cruising_speed_symbol} ${cruising_speed_unit}`}
										</TextNoAnimation>
									</div>
									<RATypography variant="body2" top={4}>
										Cruise Speed
									</RATypography>
								</Col>
							</WrapperItem>
						</SpecificationCard>
					</PaddingRow>
				</Col>
			</WrapperContent>
		</Container>
	);
}

const WrapperItem = styled(Row)`
	width: 100%;
	padding: 24px 20px;
	background: #ffffff;
	border: 1px solid #4e4e4e;
	border-radius: 16px;
`;
const Container = styled.div`
	width: 100%;
	position: relative;
	background-image: url("/images/contact-us/contact-us-mask.png");
	min-height: 400px;
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	transition: all 0.6s;
	.odometer {
		color: #222222;
		font-family: "Gilda Display";
		font-weight: 400;
		font-size: 24px;
		height: 34px;
	}

	.odometer.odometer-auto-theme,
	.odometer.odometer-theme-minimal {
		display: -moz-inline-box;
		-moz-box-orient: vertical;
		display: inline-block;
		vertical-align: middle;
		*vertical-align: auto;
		position: relative;
	}
	.odometer.odometer-auto-theme,
	.odometer.odometer-theme-minimal {
		*display: inline;
	}
	.odometer.odometer-auto-theme .odometer-digit,
	.odometer.odometer-theme-minimal .odometer-digit {
		display: -moz-inline-box;
		-moz-box-orient: vertical;
		display: inline-block;
		vertical-align: middle;
		*vertical-align: auto;
		position: relative;
		margin-right: 1px;
	}
	.odometer.odometer-auto-theme .odometer-digit,
	.odometer.odometer-theme-minimal .odometer-digit {
		*display: inline;
	}
	.odometer.odometer-auto-theme .odometer-digit .odometer-digit-spacer,
	.odometer.odometer-theme-minimal .odometer-digit .odometer-digit-spacer {
		display: -moz-inline-box;
		-moz-box-orient: vertical;
		display: inline-block;
		vertical-align: middle;
		*vertical-align: auto;
		visibility: hidden;
	}
	.odometer.odometer-auto-theme .odometer-digit .odometer-digit-spacer,
	.odometer.odometer-theme-minimal .odometer-digit .odometer-digit-spacer {
		*display: inline;
	}
	.odometer.odometer-auto-theme .odometer-digit .odometer-digit-inner,
	.odometer.odometer-theme-minimal .odometer-digit .odometer-digit-inner {
		text-align: left;
		display: block;
		position: absolute;
		top: 0;
		left: 0;
		right: -2px;
		bottom: 0;
		overflow: hidden;
	}
	.odometer.odometer-auto-theme .odometer-digit .odometer-ribbon,
	.odometer.odometer-theme-minimal .odometer-digit .odometer-ribbon {
		display: block;
	}
	.odometer.odometer-auto-theme .odometer-digit .odometer-ribbon-inner,
	.odometer.odometer-theme-minimal .odometer-digit .odometer-ribbon-inner {
		display: block;
		-webkit-backface-visibility: hidden;
	}
	.odometer.odometer-auto-theme .odometer-digit .odometer-value,
	.odometer.odometer-theme-minimal .odometer-digit .odometer-value {
		display: block;
		-webkit-transform: translateZ(0);
	}
	.odometer.odometer-auto-theme .odometer-digit .odometer-value.odometer-last-value,
	.odometer.odometer-theme-minimal .odometer-digit .odometer-value.odometer-last-value {
		position: absolute;
	}
	.odometer.odometer-auto-theme.odometer-animating-up .odometer-ribbon-inner,
	.odometer.odometer-theme-minimal.odometer-animating-up .odometer-ribbon-inner {
		-webkit-transition: -webkit-transform 2s;
		-moz-transition: -moz-transform 2s;
		-ms-transition: -ms-transform 2s;
		-o-transition: -o-transform 2s;
		transition: transform 2s;
	}
	.odometer.odometer-auto-theme.odometer-animating-up.odometer-animating .odometer-ribbon-inner,
	.odometer.odometer-theme-minimal.odometer-animating-up.odometer-animating .odometer-ribbon-inner {
		-webkit-transform: translateY(-100%);
		-moz-transform: translateY(-100%);
		-ms-transform: translateY(-100%);
		-o-transform: translateY(-100%);
		transform: translateY(-100%);
	}
	.odometer.odometer-auto-theme.odometer-animating-down .odometer-ribbon-inner,
	.odometer.odometer-theme-minimal.odometer-animating-down .odometer-ribbon-inner {
		-webkit-transform: translateY(-100%);
		-moz-transform: translateY(-100%);
		-ms-transform: translateY(-100%);
		-o-transform: translateY(-100%);
		transform: translateY(-100%);
	}
	.odometer.odometer-auto-theme.odometer-animating-down.odometer-animating .odometer-ribbon-inner,
	.odometer.odometer-theme-minimal.odometer-animating-down.odometer-animating .odometer-ribbon-inner {
		-webkit-transition: -webkit-transform 2s;
		-moz-transition: -moz-transform 2s;
		-ms-transition: -ms-transform 2s;
		-o-transition: -o-transform 2s;
		transition: transform 2s;
		-webkit-transform: translateY(0);
		-moz-transform: translateY(0);
		-ms-transform: translateY(0);
		-o-transform: translateY(0);
		transform: translateY(0);
	}
`;
const WrapperContent = styled(Row)`
	width: 100%;
	height: 100%;
	padding: 50px;
	@media (max-width: 768px) {
		padding: 16px;
	}
`;

const SpecificationCard = styled(Col)`
	padding: 12px;
	@media (max-width: 576px) {
		padding: 6px 0px;
	}
`;

const TextNoAnimation = styled.span`
	color: #222222;
	font-family: "Gilda Display";
	font-weight: 400;
	font-size: 24px;
	line-height: 34px;
`;

const PaddingRow = styled(Row)`
	padding: 0 20px;
	@media (max-width: 576px) {
		padding: 10px 0px;
	}
`;
